import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  MerchantDataResponse,
  OfferMerchantListResponse,
} from '@pv-frontend/gift-voucher-journey/interfaces/offer.interface';
import { DeviceService } from '@pv-frontend/pv-shared-services/device-service';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ModalService } from 'projects/visa-platform/src/app/shared/services/modal.service';
import { environment } from 'projects/visa-platform/src/environments/environment';
import { Environment } from 'projects/visa-platform/src/environments/environment.interface';
import { Subject, Subscription, debounceTime, finalize, takeUntil } from 'rxjs';

import { ApiService } from '../../services/api.service';

@Component({
  selector: 'visa-platform-search-bar',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule],
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.scss',
})
export class SearchBarComponent implements OnDestroy, OnInit {
  private destroy$: Subject<void> = new Subject<void>();
  public showDrawer: boolean = false;
  public searchInput: string = '';
  private searchSubject: Subject<string> = new Subject();
  private reloadSubscription?: Subscription;
  public searchResults?: MerchantDataResponse[];
  public searching: boolean = false;
  public env: Environment = environment;

  public constructor(
    private modalService: ModalService,
    private apiService: ApiService,
    public deviceService: DeviceService,
    private router: Router,
    private messageService: MessageService
  ) {
    this.modalService.searchBarVisible$.subscribe(
      (state: boolean) => (this.showDrawer = state)
    );
  }

  public ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(700)).subscribe((searchTextValue) => {
      if (this.reloadSubscription) {
        this.reloadSubscription.unsubscribe();
      }
      if (searchTextValue.length > 2) {
        this.getMerchants(searchTextValue);
      }
    });
  }

  public debouncedPopulateMerchantList(searchTextValueEvent: Event): void {
    this.searchSubject.next(
      (searchTextValueEvent?.target as HTMLInputElement)?.value
    );
  }

  private getMerchants(name: string): void {
    this.searching = true;
    this.reloadSubscription = this.apiService
      .searchMerchants(name)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.searching = false;
        })
      )
      .subscribe({
        next: (res: OfferMerchantListResponse) => {
          if (res && res.data) {
            this.searchResults = res.data.slice(0, 7);
            this.searching = false;
          } else {
            this.searching = false;
            this.searchResults = [];
          }
        },
        error: () => {
          this.searching = false;
          this.searchResults = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.env.staticTextContents.commonTexts.errorMsgs.tryLater,
            life: 3000,
          });
        },
      });
  }

  public handleClose(): void {
    this.modalService.hideSearchBarModal();
    this.searchInput = '';
    this.searchResults = [];
  }

  public navigateToDetailsPage(merchantId: string): void {
    this.handleClose();
    this.router.navigate(['/offer-details/', merchantId]);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
